import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { jobsAPI } from '@/lib/api';
import { formatDate, getStatusColor, getJobTypeColor, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  Briefcase, 
  Filter,
  Calendar,
  MapPin,
  Package,
  User,
  CalendarPlus,
  CalendarMinus,
  MoreVertical
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function AdminJobs() {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  
  // Rental modification dialogs
  const [extendDialog, setExtendDialog] = useState({ open: false, job: null });
  const [endEarlyDialog, setEndEarlyDialog] = useState({ open: false, job: null });
  const [processing, setProcessing] = useState(false);
  
  // Form state for dialogs
  const [extendDate, setExtendDate] = useState('');
  const [endEarlyDate, setEndEarlyDate] = useState('');
  const [endEarlyReason, setEndEarlyReason] = useState('');

  useEffect(() => {
    fetchJobs();
  }, [typeFilter, statusFilter, dateFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.job_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFilter) params.date = dateFilter;
      
      const response = await jobsAPI.getAll(params);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openExtendDialog = (job) => {
    // Set default date to 1 week after current end date
    const currentEnd = new Date(job.rental_end_date);
    currentEnd.setDate(currentEnd.getDate() + 7);
    setExtendDate(currentEnd.toISOString().split('T')[0]);
    setExtendDialog({ open: true, job });
  };

  const openEndEarlyDialog = (job) => {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setEndEarlyDate(tomorrow.toISOString().split('T')[0]);
    setEndEarlyReason('');
    setEndEarlyDialog({ open: true, job });
  };

  const handleExtendRental = async () => {
    if (!extendDialog.job || !extendDate) return;
    
    setProcessing(true);
    try {
      const response = await jobsAPI.extendRental(extendDialog.job.id, {
        new_end_date: extendDate
      });
      
      toast.success(
        `Rental extended to ${extendDate}. ${response.data.service_jobs_added} service job(s) added.`
      );
      setExtendDialog({ open: false, job: null });
      fetchJobs();
    } catch (error) {
      console.error('Error extending rental:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend rental');
    } finally {
      setProcessing(false);
    }
  };

  const handleEndRentalEarly = async () => {
    if (!endEarlyDialog.job || !endEarlyDate) return;
    
    setProcessing(true);
    try {
      const response = await jobsAPI.endRentalEarly(endEarlyDialog.job.id, {
        new_end_date: endEarlyDate,
        reason: endEarlyReason
      });
      
      toast.success(
        `Rental ended early on ${endEarlyDate}. ${response.data.service_jobs_cancelled} service job(s) cancelled.`
      );
      setEndEarlyDialog({ open: false, job: null });
      fetchJobs();
    } catch (error) {
      console.error('Error ending rental early:', error);
      toast.error(error.response?.data?.detail || 'Failed to end rental early');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout title="Jobs">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[130px]" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-slate-400" />
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-auto"
            data-testid="date-filter"
          />
        </div>

        {(typeFilter !== 'all' || statusFilter !== 'all' || dateFilter) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setTypeFilter('all');
              setStatusFilter('all');
              setDateFilter('');
            }}
            data-testid="clear-filters-btn"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : jobs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Briefcase className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Jobs Found</h3>
            <p className="text-slate-600">Try adjusting your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card 
              key={job.id}
              className="border-slate-200 hover:border-slate-300 transition-colors"
              data-testid={`job-card-${job.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge className={getJobTypeColor(job.job_type)} variant="outline">
                        {job.job_type}
                      </Badge>
                      <Badge className={getStatusColor(job.status)} variant="outline">
                        {job.status.replace('_', ' ')}
                      </Badge>
                      {job.rental_end_date && job.job_type === 'delivery' && (
                        <Badge variant="outline" className="text-slate-500 border-slate-300">
                          Rental ends: {formatDate(job.rental_end_date)}
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-2">{job.customer_name}</h3>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2 text-slate-600">
                        <MapPin size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
                        <span>{job.site_address}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar size={14} className="text-slate-400" />
                        <span>{formatDate(job.scheduled_date)}</span>
                        {job.scheduled_time_window && (
                          <span className="text-slate-500">• {job.scheduled_time_window}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Package size={14} className="text-slate-400" />
                        <span>{job.units_requested} {job.unit_type} unit{job.units_requested > 1 ? 's' : ''}</span>
                        {job.assigned_units?.length > 0 && (
                          <span className="text-green-600">({job.assigned_units.length} assigned)</span>
                        )}
                      </div>
                      {job.driver_name && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <User size={14} className="text-slate-400" />
                          <span>Driver: {job.driver_name}</span>
                        </div>
                      )}
                    </div>

                    {job.notes && (
                      <p className="mt-3 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                        {job.notes}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions menu for delivery jobs */}
                  {job.job_type === 'delivery' && job.status !== 'cancelled' && job.rental_end_date && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`job-actions-${job.id}`}>
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => openExtendDialog(job)}
                          data-testid={`extend-rental-${job.id}`}
                        >
                          <CalendarPlus size={14} className="mr-2" />
                          Extend Rental
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => openEndEarlyDialog(job)}
                          data-testid={`end-early-${job.id}`}
                        >
                          <CalendarMinus size={14} className="mr-2" />
                          End Rental Early
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Extend Rental Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => !processing && setExtendDialog({ open, job: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus size={20} className="text-green-600" />
              Extend Rental
            </DialogTitle>
            <DialogDescription>
              Extend the rental period for {extendDialog.job?.customer_name}. 
              This will reschedule the pickup and add any needed service jobs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-slate-500">Current end date:</span>
                <span className="font-medium">{extendDialog.job?.rental_end_date ? formatDate(extendDialog.job.rental_end_date) : 'N/A'}</span>
                <span className="text-slate-500">Units:</span>
                <span className="font-medium">{extendDialog.job?.units_requested} {extendDialog.job?.unit_type}</span>
              </div>
            </div>
            
            <div>
              <Label htmlFor="extend-date">New End Date</Label>
              <Input
                id="extend-date"
                type="date"
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
                min={extendDialog.job?.rental_end_date}
                className="mt-1"
                data-testid="extend-date-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Service jobs will be automatically added based on the rental's service frequency.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setExtendDialog({ open: false, job: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleExtendRental}
              disabled={processing || !extendDate}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-extend-btn"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Extend Rental
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Rental Early Dialog */}
      <Dialog open={endEarlyDialog.open} onOpenChange={(open) => !processing && setEndEarlyDialog({ open, job: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarMinus size={20} className="text-amber-600" />
              End Rental Early
            </DialogTitle>
            <DialogDescription>
              End the rental early for {endEarlyDialog.job?.customer_name}. 
              This will reschedule the pickup and cancel unnecessary service jobs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-slate-500">Current end date:</span>
                <span className="font-medium">{endEarlyDialog.job?.rental_end_date ? formatDate(endEarlyDialog.job.rental_end_date) : 'N/A'}</span>
                <span className="text-slate-500">Units:</span>
                <span className="font-medium">{endEarlyDialog.job?.units_requested} {endEarlyDialog.job?.unit_type}</span>
              </div>
            </div>
            
            <div>
              <Label htmlFor="end-early-date">New End Date (Pickup Date)</Label>
              <Input
                id="end-early-date"
                type="date"
                value={endEarlyDate}
                onChange={(e) => setEndEarlyDate(e.target.value)}
                min={getTodayString()}
                max={endEarlyDialog.job?.rental_end_date}
                className="mt-1"
                data-testid="end-early-date-input"
              />
            </div>
            
            <div>
              <Label htmlFor="end-early-reason">Reason (optional)</Label>
              <Textarea
                id="end-early-reason"
                value={endEarlyReason}
                onChange={(e) => setEndEarlyReason(e.target.value)}
                placeholder="Customer requested early termination..."
                rows={2}
                className="mt-1"
                data-testid="end-early-reason-input"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
              Service jobs scheduled after the new end date will be cancelled.
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEndEarlyDialog({ open: false, job: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEndRentalEarly}
              disabled={processing || !endEarlyDate}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-end-early-btn"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              End Rental Early
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
